/**
 * Lightweight client-side guard to recover from stale chunk URLs.
 *
 * When a user has an old index.html that points at a now-missing hashed chunk,
 * the browser will reject the dynamic import with
 * "Failed to fetch dynamically imported module".
 *
 * We cannot change server caching/CSP here, so we:
 * - listen for those promise rejections
 * - force a single full-page reload (per session) to fetch the fresh HTML
 * - avoid loops by remembering we already retried in sessionStorage
 */
export function registerChunkReloadGuard(): void {
  if (typeof window === "undefined") return;

  const SESSION_KEY = "chunk-reload-attempted";

  const hasAlreadyReloaded = (): boolean => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false;
    }
  };

  const markReloaded = (): void => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // Non-blocking
    }
  };

  const reloadOnce = (): void => {
    if (hasAlreadyReloaded()) return;
    markReloaded();
    window.location.reload();
  };

  const shouldHandle = (reason: unknown): boolean => {
    if (!reason) return false;

    const message =
      typeof reason === "string"
        ? reason
        : reason instanceof Error
          ? reason.message
          : String(reason);

    if (!message) return false;

    return (
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Importing a module script failed") ||
      message.includes("ChunkLoadError") ||
      message.includes("Loading chunk") ||
      message.includes("dynamic import")
    );
  };

  const handleReject = (event: PromiseRejectionEvent) => {
    if (!shouldHandle(event.reason)) return;
    reloadOnce();
  };

  window.addEventListener("unhandledrejection", handleReject);

  // Vite emits this event when a module preload fails (commonly stale hashed chunks after deploy).
  // This is the most reliable signal in many SPA navigation cases.
  window.addEventListener("vite:preloadError", () => {
    reloadOnce();
  });
}

export default registerChunkReloadGuard;
